import {print} from 'graphql/language';
import {createElement} from 'react';
import {
  get,
  set,
  isNil,
  identity,
  camelCase,
  flow,
  reduce,
  take,
  defaultsDeep,
  merge,
  isEmpty,
  initial,
  last,
} from 'lodash';
import {List, Map} from 'immutable';
import {
  fieldSlug as convertFieldSlug,
  collectionSlug as convertCollectionSlug,
  isDynamoGraphQLFieldSlug,
  convertRepeaterSlugPath,
} from '@packages/systems/dynamo/utils/SlugUtils';

import {weakMemo, cacheMemo} from '@packages/utilities/memo';
import {replaceTokens} from '@packages/systems/core/utils/EmbedUtils/all';
import {
  getIn,
  getElse,
  setIn,
  updateIn,
  toJS,
} from '@packages/systems/core/utils/ImmUtils';
import {
  getCollectionSlugMap,
  getFieldTokenOptions,
} from '@packages/systems/dynamo/utils/CollectionUtils';
import {emptyArray, constant, emptyObject} from '@packages/utilities/fp/utils';
import {itemRefTargetCollectionId} from '@packages/systems/dynamo/utils/CollectionFieldUtils';
import {testCondition} from '@packages/systems/dynamo/utils/ConditionUtils';
import {transformers} from '@packages/systems/dynamo/utils/Transformers';
import {
  reifyConditions,
  reifyCommerceOrderBindingsFromPojo,
  reifyGenericBindingsFromPojo,
  reifySearchBindingsFromPojo,
  reifyUserSubscriptionBindingsFromPojo,
} from '@packages/systems/dynamo/utils/BindingRecordUtils';

import {
  COMMERCE_ORDER_USER_ITEMS_COLLECTION_ID,
  withOrderItemsCollection,
  isProductsCollection,
} from '@packages/systems/commerce/utils/CommerceCollectionUtils';
import {
  WF_BINDING_DATA_KEY,
  WF_CONDITION_DATA_KEY,
  WF_SKU_BINDING_DATA_KEY,
  WF_SKU_CONDITION_DATA_KEY,
} from '@packages/systems/commerce/constants';
import {
  type ContextType,
  type PluginsContext,
  type AttributesMap,
  type ClassesList,
  Plugins,
  Expressions,
  ExpressionPath,
  EvalEnv,
} from '@packages/systems/wfdl';
import {type DynamoPropsInput} from '@packages/systems/dynamo/useDynamoProps';
import {
  capDynamoWrapperLimit,
  generatePaginationButtonHrefs,
  getLastPaginationPageNumber,
  getPaginateableItemsCount,
  getPaginationKeyFromNativeIdPath,
  getPaginationPageNumber,
} from '@packages/systems/dynamo/utils/PaginationUtils';
import {getIn as Imm4BackportGetIn} from '@packages/utilities/imm4-backports';
import {
  CONDITION_INVISIBLE_CLASS,
  DEFAULT_NESTED_COLLECTION_LIST_LIMIT,
} from '@packages/systems/dynamo/constants';
import {
  getItemRefSlug,
  getValueFieldSlug,
} from '@packages/systems/dynamo/utils/ParamFieldPathUtils';
import type {DeprecatedConditionsT} from '@packages/systems/dynamo/types';
import type {
  CollectionRecordType,
  Collections,
} from '@packages/systems/dynamo/records/CollectionRecord';
import type {FieldType} from '@packages/systems/dynamo/records/Field';
import type {DynamoData} from '@packages/systems/dynamo/graphql';
import type {BindingType} from '@packages/systems/core/records/Binding';
import type {SiteType} from '@packages/systems/core/records/Site';
import type {DynamoBindingProps} from '@packages/systems/dynamo/binding-props';
import {
  isDeepBindingPathPojo,
  type BindingPathMapPojo,
  type DeepBindingPathPojo,
  type ShallowBindingPathPojo,
} from '@packages/systems/dynamo/binding-types';
import {getDynTokenBindingPaths} from '@packages/systems/dynamo/utils/BindingUtils';
import {reportBug} from '@packages/utilities/reporter';
import {
  isCommerceCartItemElement,
  isCommerceCheckoutOrderItem,
  isCommerceOrderConfirmationContainer,
  isSearchBound,
} from '@packages/utilities/elementTypeUtils';
import {ExpressionElement} from '@packages/systems/wfdl-ast';
import {getName} from '@packages/systems/wfdl-Qualified';
import {TEMP_STATE_PATH} from '@packages/utilities/expression-path-constants';
import {
  isPaginatedDynamoWrapperElement,
  paginationItemsPath,
  PAGINATION_MAX_LIMIT,
  getTemporaryCollectionListItemsPreviewEnabledFromWrapper,
  getTemporaryCollectionListItemsPreviewCountFromWrapper,
  getCollectionIdFromElement,
  getCollectionItemRefSetSlugFromElement,
  getBoundRepeaterFieldSlugPathFromElement,
  getQueryLimit,
  getQueryOffset,
  getDynBindingsFromElement,
  getSearchBindingsFromElement,
  getConditionDataFromElement,
  isBoundElement,
} from '@packages/systems/dynamo/utils/DynamoElementUtils';
import {clamp} from '@packages/systems/core/utils/NumberUtils';

type DynamoContextType = ContextType;

type GetDataForElement = DynamoData['getDataForElement'];
type AdaptedGraphqlData = ReturnType<GetDataForElement>;

type GetQueryForElement = DynamoData['getQueryForElement'];
type QueryInfo = ReturnType<GetQueryForElement>;

export type UpdatedProps = {
  attributes?: AttributesMap;
  classes?: ClassesList;
  pluginsContext?: PluginsContext;
  dynamoBindingProps?: DynamoBindingProps;
};

const emptyAttributes: AttributesMap = Map();
const emptyClasses: ClassesList = List();
const constantNull = constant(null);

const getStaticBindings = (
  plugins: Plugins.PluginsType,
  elementTypeName: string
): BindingPathMapPojo => {
  const staticBindings =
    elementTypeName &&
    Plugins.__DEPRECATED__getAtoms(plugins)[elementTypeName]?.staticBindings;
  return staticBindings ? toJS(staticBindings) : emptyObject;
};

/**
 * @typedef {Object} QueryData
 * @property {Query} query
 * GraphQL query object created with `graphql-tag`
 *
 * @property {Function: (response) => (props) => newProps} propsMapperCreator
 * A function for creating propsMapper to map Apollo Data to React props
 */

// Site getters
const getTimezone: (site: SiteType) => SiteType['timezone'] = getIn([
  'timezone',
]);
const getPageLinkHrefPrefix: (
  site: SiteType
) => SiteType['pageLinkHrefPrefix'] | null = getElse(
  'pageLinkHrefPrefix',
  null
);

const dynPath: ExpressionPath = [{in: 'Record', at: 'dyn'}];

// PluginsContext getters
const getDynamoCollectionId = getIn(['Dynamo', 'collectionId']);
const getDynamoData = getElse(['Dynamo', 'dynamoData'], null);
const getPaginationPageNumbers = getElse(['Pagination', 'pageNumbers'], {});
const getSearchQuery = getIn(['Search', 'query']);
const getCmsCollectionListPreviewEnabled = getElse(
  ['Dynamo', 'cmsCollectionListPreviewEnabled'],
  false
);
const getCmsCollectionListPreviewItemsCount = getElse(
  ['Dynamo', 'cmsCollectionListPreviewItemsCount'],
  3
);
const getCmsNestedCollectionListPreviewItemsCount = getElse(
  ['Dynamo', 'cmsNestedCollectionListPreviewItemsCount'],
  3
);

// DynamoContext getters
const getCollections = getElse(['collections'], Map());
const getBindingCollection = (dynamoContext: DynamoContextType) =>
  getCollections(dynamoContext).get(dynamoContext.get('collectionId'));

// Element data getters
const isDynamoWrapperStateEmpty = (element: ExpressionElement): boolean =>
  Expressions.getElementDataAt(element, TEMP_STATE_PATH, 'Text', null) ===
  'empty';
const getDynamoWrapperLimit = (
  evaluatedDynData: unknown,
  element: ExpressionElement
): number | null => {
  if (!evaluatedDynData || evaluatedDynData === emptyObject) {
    return getQueryLimit(element);
  }
  if (
    typeof evaluatedDynData === 'object' &&
    'query' in evaluatedDynData &&
    evaluatedDynData.query &&
    typeof evaluatedDynData.query === 'object' &&
    'limit' in evaluatedDynData.query &&
    typeof evaluatedDynData.query.limit === 'number'
  ) {
    return evaluatedDynData.query.limit;
  }
  return null;
};

/**
 * Calculate the rendered items limit for a collection list wrapper.
 *
 * Priority order for determining the rendered items limit:
 * 1. Per-wrapper limits (if wrapper preview is explicitly 'True' AND limit values are set)
 * 2. Global canvas settings (if global preview is enabled AND wrapper preview is 'GlobalDefault')
 * 3. Normal capping logic (capDynamoWrapperLimit) for nested wrappers, or Infinity for top-level wrappers
 */
const calculateRenderedItemsLimit = (
  pluginsContext: PluginsContext,
  element: ExpressionElement,
  limit: number,
  isNestedWrapper: boolean,
  isConnectedToMultiReferenceField: boolean,
  isConnectedToSetField: boolean
): number => {
  // Extract global CMS preview settings from pluginsContext
  const globalCMSPreviewEnabled =
    getCmsCollectionListPreviewEnabled(pluginsContext);
  const globalCMSCollectionListPreviewItemsCount =
    getCmsCollectionListPreviewItemsCount(pluginsContext);
  const globalCMSNestedCollectionListPreviewItemsCount =
    getCmsNestedCollectionListPreviewItemsCount(pluginsContext);

  // Extract per-wrapper preview settings from element
  const wrapperCMSPreviewEnabledUnion =
    getTemporaryCollectionListItemsPreviewEnabledFromWrapper(element);
  const wrapperCMSCollectionListPreviewItemsCount =
    getTemporaryCollectionListItemsPreviewCountFromWrapper(element);

  // PRIORITY 1: Use per-wrapper limit values if:
  // - The global preview is enabled
  // - The wrapper preview is explicitly enabled ('True')
  // - AND limit values are explicitly set for this wrapper
  if (
    globalCMSPreviewEnabled &&
    wrapperCMSPreviewEnabledUnion === 'True' &&
    wrapperCMSCollectionListPreviewItemsCount != null
  ) {
    return wrapperCMSCollectionListPreviewItemsCount;
  }

  // PRIORITY 2: Fall back to global canvas settings if:
  // - Global preview is enabled
  // - AND the wrapper preview is set to 'GlobalDefault' (inherit from global)
  // This allows global settings to apply when wrapper explicitly inherits
  if (
    globalCMSPreviewEnabled &&
    wrapperCMSPreviewEnabledUnion === 'GlobalDefault'
  ) {
    return isNestedWrapper
      ? globalCMSNestedCollectionListPreviewItemsCount
      : globalCMSCollectionListPreviewItemsCount;
  }

  // PRIORITY 3: Use normal capping logic when:
  // - Wrapper preview is explicitly disabled ('False'), OR
  // - No preview settings apply
  // For nested/multi-reference lists: apply the standard cap based on the wrapper's limit
  // For top-level collection lists: no limit (Infinity)
  return isConnectedToSetField || isConnectedToMultiReferenceField
    ? capDynamoWrapperLimit(true)(isNestedWrapper)(limit)
    : Infinity;
};

const getDynamoWrapperOffset = (
  evaluatedDynData: unknown,
  element: ExpressionElement
): number => {
  if (!evaluatedDynData || evaluatedDynData === emptyObject) {
    return getQueryOffset(element) ?? 0;
  }
  if (
    typeof evaluatedDynData === 'object' &&
    'query' in evaluatedDynData &&
    evaluatedDynData.query &&
    typeof evaluatedDynData.query === 'object' &&
    'offset' in evaluatedDynData.query &&
    typeof evaluatedDynData.query.offset === 'number'
  ) {
    return evaluatedDynData.query.offset || 0;
  }
  return 0;
};

// PluginsContext updaters
type PluginsContextUpdater = (
  updater: (val: any) => any
) => (pluginsContext: PluginsContext) => PluginsContext;

function buildPluginsContextUpdater(path: string[]): PluginsContextUpdater {
  return function (updater) {
    return function (pluginsContext) {
      return pluginsContext.updateIn(path, updater);
    };
  };
}

const setPrivateCollectionIdForDynamoList = setIn(['Dynamo', '__collectionId']);
const setDynamoListItemIds = setIn(['Dynamo', 'listItemIds']);
const setDynamoCachedItems = setIn(['Dynamo', 'cachedItems']);
const setDynamoPaginateableItemsCount = setIn(['Dynamo', 'totalItems']);
const setDynamoTotalPagesCount = setIn(['Dynamo', 'totalPages']);
const setCommerceData = setIn(['Commerce', 'data']);
const setCommerceQuery = setIn(['Commerce', 'query']);
const setCommerceSampleItems = setIn(['Commerce', 'sampleItems']);
const updateDynamoItemId = buildPluginsContextUpdater(['Dynamo', 'itemId']);
const setDynamoRepeaterSlugPath = setIn(['Dynamo', 'repeaterSlugPath']);
const updateDynamoRepeaterItemsCount = buildPluginsContextUpdater([
  'Dynamo',
  'repeaterItemsCount',
]);
const setDynamoContextItem = setIn(['Dynamo', 'contextItem']);
const setPaginationPreviousHref = setIn([
  'Pagination',
  'paginationPreviousHref',
]);
const setPaginationNextHref = setIn(['Pagination', 'paginationNextHref']);
const setPaginationPageNumber = setIn(['Pagination', 'pageNumber']);
const setPaginationTotalPagesCount = setIn(['Pagination', 'totalPages']);

// DynamoContext updaters
const addOrderItemsCollection = updateIn(withOrderItemsCollection, [
  'collections',
]);
const setOrderItemsCollectionId = setIn(
  ['collectionId'],
  COMMERCE_ORDER_USER_ITEMS_COLLECTION_ID
);
const dynamoContextWithOrderItems = flow(
  addOrderItemsCollection,
  setOrderItemsCollectionId
);

const toExpressionPath = (path: Array<string>): ExpressionPath =>
  path.map((key) => ({in: 'Record', at: key}));

const isElementWithReplaceableTokens = (
  element: ExpressionElement,
  pathToReplaceableString: Array<string>
) => {
  const expression =
    pathToReplaceableString.length > 0
      ? Expressions.getIn(
          Expressions.getElementData(element),
          toExpressionPath(pathToReplaceableString)
        )
      : undefined;
  const replaceable = expression ? Expressions.getText(expression) : '';
  return (
    getDynTokenBindingPaths(replaceable, getName(element.type) || '').length > 0
  );
};

type FlattenedCustomData = {
  additionalTextArea?: any;
  additionalTextInput?: any;
  additionalCheckbox?: any;
};

const flattenCustomData = (customData: any): FlattenedCustomData =>
  reduce<any, FlattenedCustomData>(
    customData,
    (flattenedData, data) => {
      if (data.textArea) {
        flattenedData.additionalTextArea = data.textArea;
      } else if (data.textInput) {
        flattenedData.additionalTextInput = data.textInput;
      } else if (data.checkbox !== null) {
        flattenedData.additionalCheckbox = data.checkbox;
      }
      return flattenedData;
    },
    {}
  );

// GraphQL data updaters
const flattenOrderData = weakMemo((orderData: any) => {
  const customData = orderData.customData || [];
  const availableShippingMethods = orderData.availableShippingMethods || [];

  const selectedShipping = availableShippingMethods.find(
    ({selected}: any) => selected === true
  );
  const shippingMethodName = selectedShipping && selectedShipping.name;
  const shippingMethodDescription =
    selectedShipping && selectedShipping.description;

  const flattenedOrderData = {
    ...orderData,
    shippingMethodName,
    shippingMethodDescription,
    ...flattenCustomData(customData),
  };

  return reduce(
    orderDataReplacementPaths,
    (flattenedData, oldKeyPath, key) => {
      flattenedData[key] = get(flattenedData, oldKeyPath);
      return flattenedData;
    },
    flattenedOrderData
  );
});

const orderDataReplacementPaths = {
  cardProvider: ['customerInfo', 'stripePayment', 'card', 'provider'],
  cardLastFour: ['customerInfo', 'stripePayment', 'card', 'last4'],
  cardExpiresMonth: [
    'customerInfo',
    'stripePayment',
    'card',
    'expires',
    'month',
  ],
  cardExpiresYear: ['customerInfo', 'stripePayment', 'card', 'expires', 'year'],
  customerEmail: ['customerInfo', 'identity', 'email'],
  shippingAddressAddressee: ['customerInfo', 'shippingAddress', 'addressee'],
  shippingAddressLine1: ['customerInfo', 'shippingAddress', 'line1'],
  shippingAddressLine2: ['customerInfo', 'shippingAddress', 'line2'],
  shippingAddressCity: ['customerInfo', 'shippingAddress', 'city'],
  shippingAddressState: ['customerInfo', 'shippingAddress', 'state'],
  shippingAddressCountry: ['customerInfo', 'shippingAddress', 'country'],
  shippingAddressPostalCode: ['customerInfo', 'shippingAddress', 'postalCode'],
  billingAddressAddressee: ['customerInfo', 'billingAddress', 'addressee'],
  billingAddressLine1: ['customerInfo', 'billingAddress', 'line1'],
  billingAddressLine2: ['customerInfo', 'billingAddress', 'line2'],
  billingAddressCity: ['customerInfo', 'billingAddress', 'city'],
  billingAddressPostalCode: ['customerInfo', 'billingAddress', 'postalCode'],
  billingAddressState: ['customerInfo', 'billingAddress', 'state'],
  billingAddressCountry: ['customerInfo', 'billingAddress', 'country'],
  requiresShipping: ['statusFlags', 'requiresShipping'],
  hasDownloads: ['statusFlags', 'hasDownloads'],
} as const;

/*
  We add a field alias to the order confirmation wrapper query
  that is necessary to prevent the cart wrapper query from
  conflicting with the order confirmation query when a cart
  element is present on the order confirmation page.

  This alias would break published site rendering scripts, because
  they rely on the actual field names from the schema being present
  in the data returned from the GraphQL API.

  Therefore, we need to strip any aliases added by the query path
  specifier from the query string served in published site HTML.
*/
const printCommerceQueryStringForPublishedSite = (
  element: ExpressionElement,
  query: QueryInfo['query']
) => {
  if (query === null) {
    return null;
  }
  const queryString = print(query);
  return isCommerceOrderConfirmationContainer(element)
    ? queryString.replace('orderConfirmationCommerceOrder: ', '')
    : queryString;
};

type SampleField = {[slug: string]: string | SampleField | SampleField[]};

export const buildCommerceOrderPropsMapperCreator = (
  commerceOrderWrapperElement: ExpressionElement,
  dynamoContext: DynamoContextType,
  plugins: Plugins.PluginsType
) => {
  const dynamoContextForOrderItemsListItem = dynamoContext.withMutations(
    dynamoContextWithOrderItems
  );

  const orderItemsListTreeElementArray =
    buildCommerceOrderItemsTreeElementArray(commerceOrderWrapperElement);

  // DynamoContext does not yet contain Order Items collection because we're not inside or on an
  // Order Items List Item so we need to mimic the dynamoContext inside or on an Order Items List Item
  // when reifying the conditions of the elements inside or on an Order Items List Item.
  const collections = getCollections(dynamoContextForOrderItemsListItem);
  const orderItemsCollection = getBindingCollection(
    dynamoContextForOrderItemsListItem
  );

  const binds = orderItemsListTreeElementArray.reduce<List<BindingType>>(
    (memo, element): List<BindingType> => {
      const elementTypeName = getName(element.type);
      const staticBindings = getStaticBindings(plugins, elementTypeName);
      const conditionData = getConditionDataFromElement(element);
      const conditionFieldsPojo = conditionData?.fields ?? {};
      return memo
        .concat<BindingType>(
          commerceOrderBindings(collections)(
            COMMERCE_ORDER_USER_ITEMS_COLLECTION_ID
          )(staticBindings)(element)
        )
        .concat<BindingType>(
          reifyConditions(orderItemsCollection)(collections)(
            conditionFieldsPojo
          )
        );
    },
    List()
  );

  const fieldNameToSampleField = (
    pathParts: string[]
  ): SampleField | string => {
    if (pathParts.length === 0) {
      return '';
    }

    // @ts-expect-error - TS2322 - Type 'string | undefined' is not assignable to type 'string'.
    const firstPathPart: string = pathParts[0];

    if (firstPathPart === 'f_sku_properties_3dr') {
      return {
        [firstPathPart]: [
          {
            id: '',
            name: '',
            enum: [{slug: '', id: '', name: ''}],
          },
        ],
      };
    }

    return {[firstPathPart]: fieldNameToSampleField(pathParts.slice(1))};
  };

  const defaultProductFields = {
    id: '',
    f_name_: '',
    f_slug_: '',
    f_description_: '',
    f_shippable_: '',
  } as const;

  const createCustomProductsFields = (bindings: List<BindingType>) =>
    bindings
      .filter(
        (binding) => binding.from != null && binding.from[0] === 'product'
      )
      .map((binding) => binding.from.slice(1))
      .reduce(
        (productFields, pathParts) =>
          merge(productFields, fieldNameToSampleField(pathParts)),
        defaultProductFields
      );

  const sampleItems = [
    {
      id: null,
      count: 1,
      rowTotal: {value: 0, unit: 'USD'},
      price: {value: 0, unit: 'USD'},
      product: createCustomProductsFields(binds),
      sku: {
        id: '',
        f_price_: {value: 0, unit: 'USD'},
        f_name_: '',
        f_slug_: '',
        f_width_: '',
        f_length_: '',
        f_height_: '',
        f_weight_: '',
        f_sku_: '',
        f_main_image_4dr: {url: ''},
        f_sku_values_3dr: [{property: {id: ''}, value: {id: ''}}],
        f_download_files_8dr: [{name: '', url: ''}],
      },
    },
  ];

  return ({data}: {data: AdaptedGraphqlData}) =>
    (originalProps: DynamoPropsInput): UpdatedProps => {
      const {originalElement, pluginsContext, components, nativeIdPath, env} =
        originalProps;
      const dynamoData = getDynamoData(pluginsContext);
      const {query} =
        dynamoData && dynamoData.getQueryForElement
          ? dynamoData.getQueryForElement(
              originalElement.id,
              nativeIdPath,
              components,
              env
            )
          : {query: null};

      // According to Flow types, `data` can be an array. Order data should never
      // be an array, so we ignore corrupt data here.
      const orderData = Array.isArray(data)
        ? null
        : get(data, ['database', 'commerceOrder']);

      const updatePluginsContext = flow(
        setCommerceData(
          // Manual data munging to get order data a bit flatter for use in the Order collection bindings
          orderData && flattenOrderData(orderData)
        ),
        // The query is passed into context so it can be rendered into markup, and
        // used on published sites
        setCommerceQuery(
          printCommerceQueryStringForPublishedSite(originalElement, query)
        ),
        // We need these sample items for generating the script template for the cart list
        setCommerceSampleItems(sampleItems)
      );

      return {
        pluginsContext:
          originalProps.pluginsContext.withMutations(updatePluginsContext),
      };
    };
};

const CHILDREN_PATH: ExpressionPath = [{in: 'Record', at: 'children'}];

const getElementChildren = (
  element: ExpressionElement
): Array<ExpressionElement> => {
  const data = Expressions.getElementData(element);
  const children = Expressions.getIn(data, CHILDREN_PATH);
  const childrenList = children ? Expressions.getList(children) : null;
  if (!childrenList) return emptyArray;
  return childrenList.reduce<Array<ExpressionElement>>((result, e) => {
    const el = Expressions.getElement(e);
    if (el) result.push(el);
    return result;
  }, []);
};

const buildCommerceOrderItemsTreeElementArray = (
  element: ExpressionElement,
  isInsideOrderItemsListItem = false
): ExpressionElement[] => {
  const insideOrderItemsListItem =
    isInsideOrderItemsListItem ||
    isCommerceCartItemElement(element) ||
    isCommerceCheckoutOrderItem(element);

  const children = getElementChildren(element);
  return children.reduce<Array<ExpressionElement>>(
    (elementArray, child) => {
      return elementArray.concat(
        buildCommerceOrderItemsTreeElementArray(child, insideOrderItemsListItem)
      );
    },
    insideOrderItemsListItem ? [element] : emptyArray
  );
};

export const buildCommerceOrderBindingAndConditionPropsMapper = (
  bindingElement: ExpressionElement,
  dynamoContext: DynamoContextType,
  site: SiteType,
  pathToReplaceableString: Array<string>,
  data: AdaptedGraphqlData,
  bindingPathPrefix: Array<string>,
  staticBindings: BindingPathMapPojo
) => {
  const cleanTokenSlug = (slug: string, index: number | null | undefined) =>
    index === 0 ? slug : convertFieldSlug(slug);

  const collections = getCollections(dynamoContext);
  const collectionId = dynamoContext.get('collectionId');
  const getBindings =
    commerceOrderBindings(collections)(collectionId)(staticBindings);

  // Even if the data is not available, we still want to add
  // the bindingTemplatePaths and the conditionTemplatePaths.
  const propsMapper = !isEmpty(data)
    ? createBindingAndConditionPropsMapperCreator(
        bindingElement,
        dynamoContext,
        site,
        pathToReplaceableString,
        cleanTokenSlug,
        getBindings,
        false,
        ''
      )({data})
    : constantNull;

  const collection = getBindingCollection(dynamoContext);
  const collectionSlugMap = getCollectionSlugMap(collections);
  const timezone = getTimezone(site);
  const pageLinkHrefPrefix = getPageLinkHrefPrefix(site);
  const bindings = getBindings(bindingElement);
  const conditionData = getConditionDataFromElement(bindingElement);
  const conditionFieldsPojo = conditionData?.fields ?? {};
  const conditions =
    reifyConditions(collection)(collections)(conditionFieldsPojo);
  const linkSubject = Expressions.getElementDataAt(
    bindingElement,
    toExpressionPath(['link', 'subject']),
    'Text',
    undefined
  );
  const bindingTemplatePaths = encodeURIComponent(
    JSON.stringify(
      bindings
        .map((binding) => {
          const filterType = binding.filter.type;
          const toPathString = binding.to.join('.');
          return {
            // TODO: valuePath logic to be updated to support ItemRef fields through CartItem
            [toPathString]: {
              type: binding.type,
              filter: binding.filter,
              dataPath: bindingPathPrefix.concat(binding.from).join('.'),
              // timezone is only included for Date because it is only needed in the `date` filter. Otherwise omitted to keep page size down.
              timezone: filterType === 'date' ? timezone : undefined,
              // pageLinkHrefPrefix is only included for RichText because it is only needed in the `rich` filter. Otherwise omitted to keep page size down.
              pageLinkHrefPrefix:
                filterType === 'rich' ? pageLinkHrefPrefix : undefined,
              // collectionSlugMap is only included for `rich` and `detailPage` filters because only those need it. Otherwise omitted to keep page size down.
              collectionSlugMap:
                filterType === 'rich' || filterType === 'detailPage'
                  ? collectionSlugMap
                  : undefined,
              // emailLinkSubject is only included for href-bound Email bindings on Link elements that have the subject. Otherwise omitted to keep page size down.
              emailLinkSubject:
                toPathString === 'href' &&
                binding.type === 'Email' &&
                getName(bindingElement.type) === 'Link' &&
                linkSubject
                  ? linkSubject
                  : undefined,
            },
          };
        })
        .toJS()
    )
  );

  const conditionsNeedTimezone = conditions.some(
    (condition) => condition.type === 'Date'
  );

  // this communicates to published site frontends that
  // these conditions pertain to Product Option properties
  // i.e. Option Set names and values
  const isCommercePropCondition =
    bindingPathPrefix.indexOf('f_sku_properties_3dr[]') >= 0;

  return (props: DynamoPropsInput): UpdatedProps | null => {
    const updatedProps = propsMapper(props);
    const attributes = updatedProps?.attributes || Map();
    const newAttributes = attributes
      .set(WF_BINDING_DATA_KEY, bindingTemplatePaths)
      // Using update so that we only store `WF_CONDITION_DATA_KEY`
      // when the element has conditional visibility.
      .update(WF_CONDITION_DATA_KEY, (original) =>
        conditions.size > 0
          ? encodeURIComponent(
              JSON.stringify({
                condition: addFieldTypesToConditions(
                  conditionData ?? {fields: {}},
                  collection,
                  collections
                ),
                timezone: conditionsNeedTimezone ? timezone : undefined,
                dataPath: bindingPathPrefix.join('.'),
                meta: isCommercePropCondition
                  ? {type: 'CommercePropValues'}
                  : undefined,
              })
            )
          : original
      );

    return {...updatedProps, attributes: newAttributes};
  };
};

export const buildSearchBindingPropsMapper = (
  bindingElement: ExpressionElement,
  dynamoContext: DynamoContextType,
  site: SiteType,
  pathToReplaceableString: Array<string>,
  data: AdaptedGraphqlData,
  highlightingEnabled: boolean,
  publishTarget: string
) => {
  const cleanTokenSlug = camelCase;
  return createBindingAndConditionPropsMapperCreator(
    bindingElement,
    dynamoContext,
    site,
    pathToReplaceableString,
    cleanTokenSlug,
    searchBindings,
    highlightingEnabled,
    publishTarget
  )({data});
};

export const buildUserSubscriptionBindingPropsMapper = (
  bindingElement: ExpressionElement,
  dynamoContext: DynamoContextType,
  site: SiteType,
  plugins: Plugins.PluginsType,
  pathToReplaceableString: Array<string>,
  data: AdaptedGraphqlData,
  bindingPathPrefix: Array<string>
) => {
  const staticBindings = getStaticBindings(
    plugins,
    getName(bindingElement.type)
  );

  const collections = getCollections(dynamoContext);
  const collection = getBindingCollection(dynamoContext);
  const collectionId = dynamoContext.get('collectionId');

  const getBindings =
    userSubscriptionBindings(collections)(collectionId)(staticBindings);

  const propsMapper = createBindingAndConditionPropsMapperCreator(
    bindingElement,
    dynamoContext,
    site,
    pathToReplaceableString,
    convertFieldSlug,
    getBindings,
    false,
    ''
  )({data});

  // TODO: refactor this out to a shared function
  const collectionSlugMap = getCollectionSlugMap(collections);
  const timezone = getTimezone(site);
  const pageLinkHrefPrefix = getPageLinkHrefPrefix(site);
  const bindings = getBindings(bindingElement);
  const conditionData = getConditionDataFromElement(bindingElement);
  const conditionFieldsPojo = conditionData?.fields ?? {};
  const conditions =
    reifyConditions(collection)(collections)(conditionFieldsPojo);
  const conditionsNeedTimezone = conditions.some(
    (condition) => condition.type === 'Date'
  );

  const bindingTemplatePaths = encodeURIComponent(
    JSON.stringify(
      bindings
        .map((binding) => {
          const filterType = binding.filter.type;
          const toPathString = binding.to.join('.');
          return {
            [toPathString]: {
              type: binding.type,
              filter: binding.filter,
              dataPath: bindingPathPrefix.concat(binding.from).join('.'),
              // timezone is only included for Date because it is only needed in the `date` filter. Otherwise omitted to keep page size down.
              timezone: filterType === 'date' ? timezone : undefined,
              // pageLinkHrefPrefix is only included for RichText because it is only needed in the `rich` filter. Otherwise omitted to keep page size down.
              pageLinkHrefPrefix:
                filterType === 'rich' ? pageLinkHrefPrefix : undefined,
              // collectionSlugMap is only included for `rich` and `detailPage` filters because only those need it. Otherwise omitted to keep page size down.
              collectionSlugMap:
                filterType === 'rich' || filterType === 'detailPage'
                  ? collectionSlugMap
                  : undefined,
            },
          };
        })
        .toJS()
    )
  );

  return (props: DynamoPropsInput): UpdatedProps => {
    const newAttributes: AttributesMap = emptyAttributes
      .set(WF_BINDING_DATA_KEY, bindingTemplatePaths)
      // Using update so that we only store `WF_CONDITION_DATA_KEY`
      // when the element has conditional visibility.
      .update(WF_CONDITION_DATA_KEY, (original) =>
        conditions.size > 0
          ? encodeURIComponent(
              JSON.stringify({
                condition: addFieldTypesToConditions(
                  conditionData ?? {fields: {}},
                  collection,
                  collections
                ),
                timezone: conditionsNeedTimezone ? timezone : undefined,
                dataPath: bindingPathPrefix.join('.'),
              })
            )
          : original
      );

    return {...propsMapper(props), attributes: newAttributes};
  };
};

/**
 * Builds a props mapper that maps data to element bindings and conditional visibility.
 *
 * @param  {ExpressionElement} bindingElement
 * Element with dynamic binding.
 *
 * @param  {Map()} dynamoContext
 * Dynamo Plugin context.
 *
 * @param  {SiteType} site
 * Site.
 *
 * @param  {Plugins.PluginsType} plugins
 * Plugins.
 *
 * @param  {Array} pathToReplaceableString
 * For token replacement, path to a value in element data that is to be replaced.
 *
 * @return {PropsMapper} a propsMapperCreator function
 */
export const buildBindingAndConditionPropsMapperCreator = (
  bindingElement: ExpressionElement,
  dynamoContext: DynamoContextType,
  site: SiteType,
  plugins: Plugins.PluginsType,
  pathToReplaceableString: Array<string>
) => {
  const staticBindings = getStaticBindings(
    plugins,
    getName(bindingElement.type)
  );

  const collections = getCollections(dynamoContext);
  const collectionId = dynamoContext.get('collectionId');

  return createBindingAndConditionPropsMapperCreator(
    bindingElement,
    dynamoContext,
    site,
    pathToReplaceableString,
    convertFieldSlug,
    genericBindings(collections)(collectionId)(staticBindings),
    false,
    ''
  );
};

const highlightText = (snippet: any, index: number) => {
  return snippet && snippet.isHighlighted
    ? createElement(
        'strong',
        {key: index},
        typeof snippet.value === 'string' ? snippet.value : ''
      )
    : nonHighlightText(snippet, index);
};

const nonHighlightText = (snippet: any, index: number) => {
  return snippet
    ? createElement(
        'span',
        {key: index},
        typeof snippet.value === 'string' ? snippet.value : ''
      )
    : null;
};

const ellipsis = createElement('span', {key: 'ellipsis'}, '…');

type GetBindings = (element: ExpressionElement) => List<BindingType>;

const createBindingAndConditionPropsMapperCreator = (
  bindingElement: ExpressionElement,
  dynamoContext: DynamoContextType,
  site: SiteType,
  pathToReplaceableString: Array<string>,
  cleanTokenSlug: (arg1: string, arg2?: number | null | undefined) => string,
  getBindings: GetBindings,
  highlightingEnabled: boolean,
  publishTarget: string
) => {
  const timezone = getTimezone(site);
  const pageLinkHrefPrefix = getPageLinkHrefPrefix(site);

  const hasCondition = Boolean(getConditionDataFromElement(bindingElement));
  const hasBindingContext = Boolean(dynamoContext.get('collectionId'));
  const hasDynamicTokens = isElementWithReplaceableTokens(
    bindingElement,
    pathToReplaceableString
  );

  if (
    hasBindingContext &&
    (hasCondition ||
      getBindings(bindingElement).size > 0 ||
      isSearchBound(bindingElement) ||
      hasDynamicTokens)
  ) {
    return ({data}: {data: AdaptedGraphqlData}) =>
      (props: DynamoPropsInput): UpdatedProps | null => {
        const item =
          // We can't continue if we don't have dynamic item so returning null, which
          // means no props need to change.
          !data ||
          // According to Flow types, `data` can be an array. Binding data should never
          // be an array, so we ignore corrupt data here.
          Array.isArray(data)
            ? null
            : data;

        if (!item) {
          return null;
        }

        const {originalElement, pluginsContext} = props;
        const newDynamoContext = pluginsContext.get('Dynamo', Map());
        const newCollections = getCollections(newDynamoContext);
        const newCollection = getBindingCollection(newDynamoContext);
        const bindings = getBindings(originalElement);

        const currencySettings = pluginsContext.getIn([
          'Commerce',
          'ecommCurrencySettings',
        ]);

        const repeaterSlugPath = newDynamoContext.get('repeaterSlugPath', []);

        const initialBindingProps: DynamoBindingProps = {};

        // For some reason, we have HtmlEmbeds that have dynamic variable tokens
        // but no `token` bindings. Still, for backward compatibility, we need to
        // replace those variable tokens so we're using `isElementWithReplaceableTokens`
        // to detect such HtmlEmbeds.
        if (hasDynamicTokens) {
          const replaceable =
            Expressions.getElementDataAt(
              originalElement,
              toExpressionPath(pathToReplaceableString),
              'Text',
              ''
            ) || '';
          const tokenOptions = getFieldTokenOptions(
            newCollection,
            newCollections,
            {
              singleOption:
                repeaterSlugPath.length > 0 ? repeaterSlugPath : null,
            }
          );

          if (tokenOptions) {
            initialBindingProps.token = replaceTokens(replaceable, item, {
              options: tokenOptions,
              cleanSlug: cleanTokenSlug,
              // Custom code is rendered with dangerouslySetInnerHTML so we're escaping
              // the token values by default. However, title and meta tags are rendered
              // normally with React so the values will be escaped by React. We're not
              // escaping title or meta tag values so that we wouldn't be escaping twice,
              // e.g. '"' -> '&quot;' -> '&amp;quot;'.
              dangerouslyUnescaped:
                getName(originalElement.type) === 'Title' ||
                getName(originalElement.type) === 'Meta',
              timezone,
              currencySettings,
            });
          }
        }

        const bindingProps = bindings.reduce((bindingPropsMemo, binding) => {
          if (binding.to[0] === 'token') {
            // `initialBindingProps` contains token binding props already
            return bindingPropsMemo;
          } else {
            // Regular binding
            const rawValue = get(item, binding.from);
            const transformedValue =
              transformers(rawValue, binding.filter, {
                timezone,
                pageLinkHrefPrefix,
                collectionSlugMap: getCollectionSlugMap(newCollections),
                currencySettings,
              }) ?? null;

            if (
              binding.type === 'HighlightedText' &&
              Array.isArray(transformedValue)
            ) {
              const highlighted = highlightingEnabled
                ? transformedValue.map(highlightText)
                : transformedValue.map(nonHighlightText);
              // Always append ellipsis
              const withEllipsis = highlighted.concat(ellipsis);
              return set(bindingPropsMemo, binding.to, withEllipsis);
            } else if (
              binding.from[0] === 'pageUrl' &&
              binding.to[0] === 'innerHTML' &&
              transformedValue
            ) {
              // Prepend domain name to pageUrl only when displayed as text (keep href bindings `/slug`)
              return set(
                bindingPropsMemo,
                binding.to,
                publishTarget + String(transformedValue)
              );
            } else if (
              binding.to[0] === 'repeater' &&
              !Array.isArray(transformedValue)
            ) {
              return bindingPropsMemo;
            }

            return set(bindingPropsMemo, binding.to, transformedValue);
          }
        }, initialBindingProps);

        const originalCondition = getConditionDataFromElement(originalElement);
        // If contextItemId is not defined item.id is the page item's id.
        // Otherwise we're inside DynamoList and item.id is list items's id.
        // It is important that we use not-found (second parameter to .get) here because
        // there's a difference between contextItemId not being set yet or it being set to null.
        const contextItemForCondition = newDynamoContext.get('contextItem') || {
          id: newDynamoContext.get('contextItemId', get(item, 'id')),
        };
        const visible = originalCondition
          ? testCondition({
              // @ts-expect-error - TS2322 - Type 'Readonly<{ [key: string]: any; }> | readonly Readonly<{ [key: string]: any; }>[]' is not assignable to type 'Readonly<{ [key: string]: unknown; }>'.
              item,
              contextItem: contextItemForCondition,
              timezone,
              condition: addFieldTypesToConditions(
                originalCondition,
                newCollection,
                newCollections
              ),
              graphQLSlugs: true,
            })
          : true;

        const hasInnerHTMLBinding = isBoundElement(
          originalElement,
          'innerHTML'
        );
        const hasMediaBinding = isBoundElement(originalElement, 'media');
        const hasSrcBinding = isBoundElement(originalElement, 'src');

        const newClasses = emptyClasses.update((classList) => {
          classList = visible
            ? classList
            : classList.push(CONDITION_INVISIBLE_CLASS);

          const hasEmptySrcBinding =
            hasSrcBinding && (!bindingProps.src || !bindingProps.src.url);
          const hasEmptyMediaBinding =
            hasMediaBinding && hasEmptyMediaDynamoBindingProp(bindingProps);
          const hasEmptyInnerHtmlTextBinding =
            hasInnerHTMLBinding &&
            (isNil(bindingProps.innerHTML) || bindingProps.innerHTML === '');
          const hasEmptyInnerHtmlVideoBinding =
            hasInnerHTMLBinding &&
            bindingProps.innerHTML &&
            typeof bindingProps.innerHTML === 'object' &&
            'metadata' in bindingProps.innerHTML &&
            bindingProps.innerHTML.metadata &&
            isNil(bindingProps.innerHTML.metadata.html);

          return hasEmptySrcBinding ||
            hasEmptyMediaBinding ||
            hasEmptyInnerHtmlTextBinding ||
            hasEmptyInnerHtmlVideoBinding
            ? classList.push('w-dyn-bind-empty')
            : classList;
        });

        const skuBindingAttribute = getSkuBindingAttr(dynamoContext, bindings);

        const newAttributes = skuBindingAttribute
          ? emptyAttributes.set(
              WF_SKU_BINDING_DATA_KEY,
              encodeURIComponent(skuBindingAttribute)
            )
          : emptyAttributes;

        const requiresSkuCondVisAttr =
          isProductsCollection(getBindingCollection(dynamoContext)) &&
          hasCondition &&
          originalCondition;

        const updatedProps: UpdatedProps = {
          attributes: requiresSkuCondVisAttr
            ? getSkuCondVisAttr(
                newCollection,
                newCollections,
                originalCondition,
                item,
                contextItemForCondition,
                timezone,
                newAttributes
              )
            : newAttributes,
          classes: newClasses,
        };

        if (Object.keys(bindingProps).length) {
          updatedProps.dynamoBindingProps = bindingProps;
        }

        return updatedProps;
      };
  } else {
    return emptyBindingAndConditionPropsMapperCreator;
  }
};

const emptyBindingAndConditionPropsMapperCreator =
  (_response: {data: AdaptedGraphqlData}) =>
  (props: DynamoPropsInput): UpdatedProps | null => {
    const {originalElement, pluginsContext} = props;
    const hasBindableCollection = Boolean(
      getDynamoCollectionId(pluginsContext)
    );

    const hasInnerHTMLBinding = isBoundElement(originalElement, 'innerHTML');
    const hasSrcBinding = isBoundElement(originalElement, 'src');

    const shouldHaveEmptyClass =
      hasBindableCollection && (hasInnerHTMLBinding || hasSrcBinding);

    // Note that we need to read conditions here from the ORIGINAL element data that also contains invalid bindings.
    const conditions = getConditionDataFromElement(originalElement);
    // Even though there were not any valid conditions, we need to hide the element if it has a "positive" condition,
    // i.e. X equals Y, X is set OR X contains Y – even if X is undefined in this case.
    const hasFailingCondition =
      conditions &&
      Object.values(conditions.fields ?? {}).some((field) => {
        return 'eq' in field || 'idin' in field || field.exists === 'yes';
      });
    const shouldHaveInvisibleClass =
      hasBindableCollection && hasFailingCondition;

    if (!shouldHaveEmptyClass && !shouldHaveInvisibleClass) {
      // do not unnecessarily mutate props
      return null;
    }

    // If src and innerHTML are bound, we add w-dyn-bind-empty. If not, we just keep going
    // normally. This is required to keep backwards compat with the legacy renderer
    const newClasses = emptyClasses.withMutations((classList) => {
      if (shouldHaveEmptyClass) {
        classList.push('w-dyn-bind-empty');
      }
      if (shouldHaveInvisibleClass) {
        classList.push(CONDITION_INVISIBLE_CLASS);
      }
      return classList;
    });

    return {classes: newClasses};
  };

const getSkuBindingAttr = (
  dynamoContext: DynamoContextType,
  bindings: List<BindingType>
) => {
  if (isProductsCollection(getBindingCollection(dynamoContext)) && bindings) {
    const skuFieldBindings = bindings.filter((binding) => {
      return (
        Array.isArray(binding.from) &&
        binding.from[0] === convertFieldSlug('default-sku') &&
        binding.from[1] !== 'id'
      );
    });
    if (skuFieldBindings.size > 0) {
      const repeaterSlugPath = convertRepeaterSlugPath(
        dynamoContext.get('repeaterSlugPath', [])
      );
      const repeaterSlug =
        repeaterSlugPath.length > 0
          ? repeaterSlugPath[repeaterSlugPath.length - 1]
          : null;

      const data = JSON.stringify(
        skuFieldBindings.map((binding) => ({
          from: binding.from
            .slice(1)
            .map((slug) => {
              return binding.type === 'Set' && slug === repeaterSlug
                ? `${slug}[]`
                : slug;
            })
            .join('.'),
          to: binding.to.join('.'),
        }))
      );

      if (data.length) {
        return data;
      }
    }
  }
};

const getSkuCondVisAttr = (
  collection: CollectionRecordType,
  collections: Collections,
  conditionData: {fields?: DeprecatedConditionsT},
  item: {
    readonly [key: string]: any;
  },
  contextItem: any,
  timezone: string,
  attributes: AttributesMap
) => {
  // Separate the product conditions from the sku bound ones, as only the latter is dynamic
  const fields = conditionData.fields ?? {};
  const skuFields: DeprecatedConditionsT = {};
  const productFields: DeprecatedConditionsT = {};
  for (const [fieldKey, fieldVal] of Object.entries(fields)) {
    if (getItemRefSlug(fieldKey) === 'default-sku') {
      skuFields[fieldKey] = fieldVal;
    } else {
      productFields[fieldKey] = fieldVal;
    }
  }
  const productConditions = {fields: productFields};
  const skuConditions = {fields: skuFields};
  // We check if item is not visible after the product conditions, since those are static
  // and we don't want to do any dynamic re evaluations for sku fields if this returns false
  const isVisibleAfterProductConditions =
    Object.keys(productFields).length > 0
      ? testCondition({
          item,
          contextItem,
          timezone,
          condition: addFieldTypesToConditions(
            productConditions,
            collection,
            collections
          ),
          graphQLSlugs: true,
        })
      : true;

  // We stamp the element so that the client can re evaluate conditions bound to sku fields
  // when the currently selected product variant changes
  return attributes.update(WF_SKU_CONDITION_DATA_KEY, (original) =>
    isVisibleAfterProductConditions && Object.keys(skuFields).length > 0
      ? encodeURIComponent(
          JSON.stringify({
            condition: addFieldTypesToConditions(
              skuConditions,
              collection,
              collections
            ),
            timezone,
          })
        )
      : original
  );
};

const collectionIdMemo = cacheMemo(100);

const createBindingReifier = (
  bindingReificationFn: (bindMap: BindingPathMapPojo) => List<BindingType>
) => {
  return weakMemo((collections: Collections) =>
    collectionIdMemo((collectionId: any) =>
      weakMemo((staticBindings: BindingPathMapPojo) =>
        weakMemo((element: ExpressionElement): List<BindingType> => {
          const collection = collections.get(collectionId);
          const validated = validBindings(
            getDynBindingsFromElement(element),
            collection,
            collections
          );
          const mergedBindMap: BindingPathMapPojo = {
            ...validated,
            ...staticBindings,
          };
          const bindings = bindingReificationFn(mergedBindMap);

          if (
            getName(element.type) === 'Video' &&
            'innerHTML' in mergedBindMap
          ) {
            // TODO Dynamo Gateway: This is a temporary special case as
            //   VideoEmbed binding does not have its own binding target
            //   and therefore, we need to differentiate Video URL innerHTML
            //   and Video embed innerHTML.
            return temporary_patchVideoEmbedBinding(bindings);
          }
          return bindings;
        })
      )
    )
  );
};

const genericBindings = createBindingReifier(reifyGenericBindingsFromPojo);

const commerceOrderBindings = createBindingReifier(
  reifyCommerceOrderBindingsFromPojo
);

const userSubscriptionBindings = createBindingReifier(
  reifyUserSubscriptionBindingsFromPojo
);

const searchBindings = weakMemo(
  (element: ExpressionElement): List<BindingType> => {
    const bindMap = getSearchBindingsFromElement(element);
    const bindings: List<BindingType> =
      Object.keys(bindMap).length > 0
        ? reifySearchBindingsFromPojo(bindMap)
        : List();

    if (getName(element.type) === 'Video' && 'innerHTML' in bindMap) {
      // TODO Dynamo Gateway: This is a temporary special case as
      //   VideoEmbed binding does not have its own binding target
      //   and therefore, we need to differentiate Video URL innerHTML
      //   and Video embed innerHTML.
      return temporary_patchVideoEmbedBinding(bindings);
    }
    return bindings;
  }
);

const temporary_patchVideoEmbedBinding = (
  bindings: List<BindingType>
): List<BindingType> => {
  return bindings.map((binding) => {
    // When Video element is innerHTML-bound to a Video field, we
    // remove the last `url` from that binding path.
    const isVideoEmbedBinding =
      binding.to[0] === 'innerHTML' &&
      binding.type === 'Video' &&
      last(binding.from) === 'url';
    return isVideoEmbedBinding ? binding.update('from', initial) : binding;
  });
};

const transformedBindingProps = (
  element: ExpressionElement,
  contextItem: any,
  collections: Collections,
  collectionId: any,
  timezone: any,
  pageLinkHrefPrefix: any,
  staticBindings: BindingPathMapPojo
): DynamoBindingProps => {
  return genericBindings(collections)(collectionId)(staticBindings)(
    element
  ).reduce((bindingPropsMemo, binding) => {
    const rawValue = get(contextItem, binding.from);
    const transformedValue = transformers(rawValue, binding.filter, {
      timezone,
      pageLinkHrefPrefix,
      collectionSlugMap: getCollectionSlugMap(collections),
    });
    return isNil(transformedValue)
      ? bindingPropsMemo
      : set(bindingPropsMemo, binding.to, transformedValue);
  }, {} as DynamoBindingProps);
};

export const searchResultWrapperPropsMapperCreator =
  ({data}: {data: AdaptedGraphqlData}) =>
  (props: DynamoPropsInput): UpdatedProps | null => {
    if (
      !data ||
      // According to Flow types, `data` can be an array. Search data should never
      // be an array, so we ignore corrupt data here.
      Array.isArray(data)
    ) {
      return null;
    }

    return {
      pluginsContext: props.pluginsContext.setIn(
        ['Search', 'searchResultsData'],
        data
      ),
    };
  };

export const buildWrapperPropsMapperCreator = (
  site: SiteType,
  plugins: Plugins.PluginsType
) => {
  const timezone = getTimezone(site);
  const pageLinkHrefPrefix = getPageLinkHrefPrefix(site);

  return ({data}: {data: AdaptedGraphqlData}) => {
    const updateProps = weakMemo((element: ExpressionElement) =>
      weakMemo((env: EvalEnv.EvalEnvType) =>
        weakMemo((pluginsContext: PluginsContext) =>
          weakMemo((nativeIdPath: string[]) => {
            const dynamoContext = pluginsContext.get('Dynamo', Map());
            const isNestedWrapper = dynamoContext.get('insideItem', false);

            const dynDataExpr = Expressions.getIn(element.data, dynPath);
            const evaluatedDynData = dynDataExpr
              ? Expressions.evaluate(env, dynDataExpr)
              : emptyObject;

            const pageNumbers = getPaginationPageNumbers(pluginsContext);
            const paginationEnabled = isPaginatedDynamoWrapperElement(element);
            const paginationItemsPerPage = getPaginationItemsPerPage(
              element,
              paginationEnabled,
              env
            );
            const paginationPageNumber = paginationEnabled
              ? getPaginationPageNumber(pageNumbers, [
                  ...nativeIdPath,
                  element.id,
                ])
              : 1;

            const collections = getCollections(dynamoContext);

            const listCollectionId = getCollectionIdFromElement(element);
            const listCollectionSlug = listCollectionId
              ? getCollectionSlug(collections, listCollectionId)
              : null;
            const listItemRefSet =
              getCollectionItemRefSetSlugFromElement(element);

            const repeaterSlugPath =
              getBoundRepeaterFieldSlugPathFromElement(element);

            const isConnectedToMultiReferenceField = Boolean(listItemRefSet);
            const isConnectedToSetField = Boolean(
              repeaterSlugPath && repeaterSlugPath.length > 0
            );

            const contextCollectionId = dynamoContext.get('collectionId');
            const contextCollection = collections.get(contextCollectionId);

            // Retrieve context item data
            const contextItem = isNestedWrapper
              ? data
              : get(data, [
                  'database',
                  'collections',
                  'bindingCollection',
                  'item',
                ]);

            const contextItemId =
              contextItem && contextCollection ? get(contextItem, 'id') : null;

            const pathToItems = (function (): string[] {
              if (isConnectedToMultiReferenceField && listItemRefSet) {
                const itemRefSetFieldSlug = convertFieldSlug(listItemRefSet);
                return isNestedWrapper
                  ? [itemRefSetFieldSlug]
                  : [
                      'database',
                      'collections',
                      (contextCollectionId &&
                        getCollectionSlug(collections, contextCollectionId)) ||
                        '',
                      'item',
                      itemRefSetFieldSlug,
                    ];
              }

              return [
                'database',
                'collections',
                listCollectionSlug || '',
                'items',
              ];
            })();

            const itemsData = get(data, pathToItems, []);

            // The base limit from the dynamo wrapper settings
            const limit = isNestedWrapper
              ? (getDynamoWrapperLimit(evaluatedDynData, element) ??
                DEFAULT_NESTED_COLLECTION_LIST_LIMIT)
              : (getDynamoWrapperLimit(evaluatedDynData, element) ?? Infinity);

            // Limits for multi-reference or multi-image lists are enforced during
            // rendering. For wrappers connected directly to CMS collections, these
            // limits are enforced at query time.
            const renderedItemsLimit = calculateRenderedItemsLimit(
              pluginsContext,
              element,
              limit,
              isNestedWrapper,
              isConnectedToMultiReferenceField,
              isConnectedToSetField
            );
            const items: any[] = take(itemsData, renderedItemsLimit);

            const listItemIds = items.map((obj) => obj.id);

            const elementTypeName = getName(element.type);
            const staticBindings = getStaticBindings(plugins, elementTypeName);

            const bindingProps = transformedBindingProps(
              element,
              contextItem && contextCollection ? contextItem : {},
              collections,
              contextCollectionId,
              timezone,
              pageLinkHrefPrefix,
              staticBindings
            );

            const updatedCachedItems = dynamoContext
              .get('cachedItems', Map())
              .withMutations((newCachedItems: Map<string, any>) => {
                items.forEach((item) => {
                  if (!item.id) return;

                  newCachedItems.update(item.id, (existing = {}) =>
                    Object.assign({}, existing, item)
                  );
                });

                if (contextItemId) {
                  newCachedItems.update(contextItemId, (existing = {}) =>
                    // use defaultsDeep to merge contextItem into cached item
                    // to bring in missing properties without overwriting anything
                    defaultsDeep({}, existing, contextItem)
                  );
                }
              });

            const filteredItemsCount =
              get(data, [
                'database',
                'collections',
                listCollectionSlug || '',
                'count',
              ]) || 0;

            const offset = getDynamoWrapperOffset(evaluatedDynData, element);
            const paginateableItemsCount = getPaginateableItemsCount(
              filteredItemsCount,
              offset,
              limit
            );
            const {previous, next} = generatePaginationButtonHrefs({
              filteredItemsCount,
              skip: offset,
              limit,
              itemsPerPage: paginationItemsPerPage,
              currentPageNumber: paginationPageNumber,
              pageNumbers: getPaginationPageNumbers(pluginsContext),
              paginationKey: getPaginationKeyFromNativeIdPath([
                ...nativeIdPath,
                element.id,
              ]),
              searchQuery: getSearchQuery(pluginsContext),
            });

            const repeaterPath: string[] =
              repeaterSlugPath && contextItemId
                ? [contextItemId, ...convertRepeaterSlugPath(repeaterSlugPath)]
                : [];
            const repeaterItems =
              repeaterPath.length > 0 &&
              Imm4BackportGetIn(updatedCachedItems, repeaterPath, null);
            const repeaterItemsCount =
              repeaterItems && Array.isArray(repeaterItems)
                ? Math.min(repeaterItems.length, renderedItemsLimit)
                : 0;

            const totalItems = Math.max(
              repeaterItemsCount,
              paginateableItemsCount,
              listItemIds.length
            );

            const totalPages = paginationEnabled
              ? getLastPaginationPageNumber(totalItems, paginationItemsPerPage)
              : null;

            const updatePluginsContext = flow(
              // DynamoWrapper shouldn't update the dynamoContext.collectionId because for
              // DynamoWrapper, DynamoList and DynamoEmpty the bindable collection is still the
              // page item collection. This is a hack to pass list collection id down from
              // DynamoWrapper to DynamoList without affecting current bindable collection.
              setPrivateCollectionIdForDynamoList(listCollectionId),
              // In SSR, we've only had page item slug but now we got also the item id from server.
              // This is needed for being able to read item data from Apollo cache that is normalized using item ids.
              updateDynamoItemId((original) => contextItemId || original),
              // Differences between listItemIds and cachedItems:
              // - listItemIds is an Immutable List of ids (string), cachedItems is a List of POJOs (the collection objects)
              // - listItemIds only contains the elements from the wrapper context, cachedItems contains those elements + any other
              // item in scope, for example, the page item if it exists. In the future, with nested dynamic contexts, cachedItems
              // will include items from many wrapper contexts.
              setDynamoListItemIds(List(listItemIds)),
              setDynamoCachedItems(updatedCachedItems),
              updateDynamoRepeaterItemsCount((original) => {
                return repeaterItems && Array.isArray(repeaterItems)
                  ? repeaterItemsCount
                  : original;
              }),
              isConnectedToSetField
                ? setDynamoRepeaterSlugPath(repeaterSlugPath)
                : identity,
              setDynamoContextItem(
                contextItem && contextCollection ? contextItem : {}
              ),
              setDynamoPaginateableItemsCount(totalItems),
              paginationEnabled && !isDynamoWrapperStateEmpty(element)
                ? setPaginationPreviousHref(previous)
                : identity,
              paginationEnabled && !isDynamoWrapperStateEmpty(element)
                ? setPaginationNextHref(next)
                : identity,
              paginationEnabled
                ? setPaginationPageNumber(paginationPageNumber)
                : identity,
              paginationEnabled
                ? setPaginationTotalPagesCount(totalPages)
                : identity,
              setDynamoTotalPagesCount(totalPages)
            );

            const updatedProps: UpdatedProps = {
              pluginsContext:
                pluginsContext.withMutations(updatePluginsContext),
            };

            if (Object.keys(bindingProps).length) {
              updatedProps.dynamoBindingProps = bindingProps;
            }

            return updatedProps;
          })
        )
      )
    );

    return (
      props: DynamoPropsInput,
      env: EvalEnv.EvalEnvType
    ): UpdatedProps | null => {
      // According to Flow types, `data` can be an array. DynamoWrapper data should never
      // be an array, so we ignore corrupt data here.
      if (Array.isArray(data)) {
        return null;
      }

      const {originalElement, pluginsContext, nativeIdPath} = props;
      return updateProps(originalElement)(env)(pluginsContext)(nativeIdPath);
    };
  };
};

/**
 * Format pagination items per page number to be within 1 and the PAGINATION_MAX_LIMIT.
 * This is done to ensure that the potential override value is within these values.
 */
function formatPaginationItemsPerPageNumber(value: number): number {
  return clamp(1, PAGINATION_MAX_LIMIT, value);
}

function getPaginationItemsPerPage(
  element: ExpressionElement,
  paginationEnabled: boolean,
  env: EvalEnv.EvalEnvType
): number {
  if (!paginationEnabled) {
    return PAGINATION_MAX_LIMIT;
  }
  const paginationLimitExpr = Expressions.getIn(
    element.data,
    paginationItemsPath
  );
  if (!paginationLimitExpr) {
    return PAGINATION_MAX_LIMIT;
  }
  const paginationLimitNumber = Expressions.getNumber(paginationLimitExpr);
  if (paginationLimitNumber) {
    return formatPaginationItemsPerPageNumber(paginationLimitNumber);
  }
  const evalData = Expressions.evaluate(env, paginationLimitExpr);
  if (typeof evalData === 'number') {
    return formatPaginationItemsPerPageNumber(evalData);
  }
  return PAGINATION_MAX_LIMIT;
}

const getFieldType = (
  slug: string,
  collection: CollectionRecordType
): string | undefined => {
  const field = getField(slug, collection);
  return field ? field.type : undefined;
};

const getField = (
  slug: string,
  collection: CollectionRecordType
): FieldType | undefined => {
  const cleanSlug = isDynamoGraphQLFieldSlug(slug)
    ? convertFieldSlug
    : identity;
  return collection.fields.find((field) => {
    return cleanSlug(field.get('slug')) === slug;
  });
};

const getCollectionSlug = (
  collections: Collections,
  collectionId: string
): string | null => {
  const collection = collections.get(collectionId);
  const collectionSlug = collection && collection.get('slug');
  return typeof collectionSlug === 'string'
    ? convertCollectionSlug(collectionSlug)
    : null;
};

export const addFieldTypesToConditions = (
  conditions: {fields?: DeprecatedConditionsT},
  collection: CollectionRecordType,
  collections: Collections
): {fields: DeprecatedConditionsT} => {
  const conditionFields = conditions.fields ?? {};
  const newFields: DeprecatedConditionsT = {};
  for (const [fieldPath, conditionValue] of Object.entries(conditionFields)) {
    const fieldType = deriveFieldType({collection, collections, fieldPath});
    newFields[fieldPath] = {...conditionValue, type: fieldType};
  }
  return {fields: newFields};
};

const deriveFieldType = ({
  collection,
  collections,
  fieldPath,
}: {
  collection: CollectionRecordType;
  collections: Collections;
  fieldPath: string;
}) => {
  const itemRefFieldSlug = getItemRefSlug(fieldPath);
  const valueFieldSlug = getValueFieldSlug(fieldPath);
  let fieldType = getFieldType(valueFieldSlug, collection) || 'PlainText';

  if (itemRefFieldSlug) {
    const itemRefField = getField(itemRefFieldSlug, collection);
    const itemRefCollectionId = itemRefField
      ? itemRefTargetCollectionId(itemRefField)
      : null;
    const itemRefCollection =
      typeof itemRefCollectionId === 'string'
        ? collections.get(itemRefCollectionId)
        : null;

    fieldType =
      (itemRefCollection && getFieldType(valueFieldSlug, itemRefCollection)) ||
      'PlainText';
  }

  return fieldType;
};

const validBindings = (
  bindMap: BindingPathMapPojo,
  collection: CollectionRecordType | null | undefined,
  collections: Collections
): BindingPathMapPojo => {
  const result: BindingPathMapPojo = {};
  for (const [key, bindingPath] of Object.entries(bindMap)) {
    if (isDeepBindingPathPojo(bindingPath)) {
      const deepBindMap = validBindings(
        bindingPath,
        collection,
        collections
      ) as DeepBindingPathPojo;
      if (Object.keys(deepBindMap).length > 0) {
        result[key] = deepBindMap;
      }
    } else if (
      key === 'token' ||
      isValidBindingPath(bindingPath, collection, collections)
    ) {
      result[key] = bindingPath;
    }
  }
  return result;
};

const isValidBindingPath = (
  bindingPath: ShallowBindingPathPojo,
  collection: CollectionRecordType | null | undefined,
  collections: Collections
) => {
  for (let i = 0; i < bindingPath.length; i++) {
    const fragment = bindingPath[i];

    if (!fragment) {
      return false;
    }

    const {id, slug, type} = fragment;

    const isMetadataFragment = id === slug && slug !== 'slug';
    if (isMetadataFragment) {
      // Some binding paths include binding metadata that is redundant for this
      // validation. For example, some `src` bindings to ImageRef fields have
      // {id: 'url', slug: 'url', type: 'ImageRef'} in the end of the binding
      // path. If we have reached the metadata, we know the binding path is valid.
      //
      // NOTE: we exclude {id: 'slug', slug: 'slug'} as that is a special case
      // of `dataWHref` binding paths, and not a metadata fragment. We want to
      // validate the `slug` fragment like any other collection field binding fragment.

      // We are in the middle of removing the legacy binding paths, and as part
      // of the process we are tracking whether we have missed some types of
      // legacy binding paths that we have been using over the years.
      trackLegacyDynamoBinding(bindingPath, i, id, slug, type);

      return true;
    }

    const isFullSlugFragment = slug === 'fullSlug' && type === 'FullSlug';
    if (isFullSlugFragment) {
      return true;
    }

    const field = slug && collection && getField(slug, collection);

    if (!field || field.slug !== slug || field.type !== type) {
      return false;
    }

    if (type === 'ItemRef') {
      const refCollectionId = itemRefTargetCollectionId(field);
      collection = refCollectionId ? collections.get(refCollectionId) : null;
    }
  }
  return true;
};

function trackLegacyDynamoBinding(
  bindingPath: ShallowBindingPathPojo,
  currentFragmentIndex: number,
  currentFragmentId?: string,
  currentFragmentSlug?: string,
  currentFragmentType?: string
) {
  const previousFragment =
    currentFragmentIndex > 0 ? bindingPath[currentFragmentIndex - 1] : null;
  const isKnownSubpathFragment =
    previousFragment &&
    previousFragment.slug === 'default-sku' &&
    previousFragment.type === 'ItemRef' &&
    currentFragmentId === 'id' &&
    currentFragmentId === currentFragmentSlug &&
    currentFragmentType === 'PlainText';

  if (!isKnownSubpathFragment) {
    reportBug(
      'LegacyDynamoBindingDetected',
      'Not a bug. We are hunting down legacy bindings.',
      {
        bindingPath,
        team: 'dynamo',
        ticket: 'https://webflow.atlassian.net/browse/PLAT-1068',
      },
      {skipConsole: true}
    );
  }
}

// check if media binding property value can be considered "empty"
function hasEmptyMediaDynamoBindingProp(
  bindingProps: DynamoBindingProps
): boolean {
  if (!bindingProps.media) {
    return true;
  }

  const hasMediaFromImageRefOrVideo = Boolean(
    'url' in bindingProps.media && bindingProps.media.url
  );

  const hasMediaFromSetOfImageRef =
    Array.isArray(bindingProps.media) && bindingProps.media.length > 0;

  return !hasMediaFromImageRefOrVideo && !hasMediaFromSetOfImageRef;
}
